/**
 * useCalendarCallbacks Hook
 * Integrates host application callbacks with calendar events
 */
import { useCallback } from 'react';
import {
  CalendarEvent,
  DayObject,
  YearlyCalendarProps,
  DaySelectCallbackData,
  EventClickCallbackData,
  ModeChangeCallbackData,
  AddEditEventCallbackData,
  DeleteEventCallbackData,
  PopupActionCallbackData,
  CustomClassCallback,
  CustomAriaLabelCallback,
  CustomIconClassCallback,
  CalendarMode,
} from '../../../contracts/YearlyCalendarContracts';

export interface UseCalendarCallbacksOptions {
  // Event callbacks matching YearlyCalendarProps
  onDateChange?: YearlyCalendarProps['onDateChange'];
  onModeChange?: YearlyCalendarProps['onModeChange'];
  onDaySelect?: YearlyCalendarProps['onDaySelect'];
  onEventClick?: YearlyCalendarProps['onEventClick'];
  onPopupLeftAction?: YearlyCalendarProps['onPopupLeftAction'];
  onAddOrEditEventClick?: YearlyCalendarProps['onAddOrEditEventClick'];
  onDeleteEventClick?: YearlyCalendarProps['onDeleteEventClick'];
  
  // Customization callbacks
  customClass?: CustomClassCallback;
  customAriaLabel?: CustomAriaLabelCallback;
  customIconClass?: CustomIconClassCallback;
}

export interface UseCalendarCallbacksReturn {
  // Wrapped callbacks with consistent signatures
  handleDayClick: (day: DayObject, events: CalendarEvent[], event: React.MouseEvent) => void;
  handleDaySelect: (day: DayObject, events: CalendarEvent[]) => void;
  handleEventClick: (calendarEvent: CalendarEvent, day: DayObject, event: React.MouseEvent) => void;
  handleDateChange: (date: Date) => void;
  handleModeChange: (mode: CalendarMode, activeDate: Date) => void;
  handleAddEventClick: (day: DayObject, event: React.MouseEvent) => void;
  handleEditEventClick: (calendarEvent: CalendarEvent, day: DayObject) => void;
  handleDeleteEventClick: (calendarEvent: CalendarEvent, day: DayObject) => void;
  handlePopupLeftAction: (date: Date, events: CalendarEvent[]) => void;
  
  // Customization helpers
  getDayCustomClass: (date: Date, mode: string) => string | undefined;
  getDayCustomAriaLabel: (date: Date, mode: string) => string | undefined;
  getDayCustomIconClass: (date: Date, mode: string) => string | undefined;
}

/**
 * Custom hook for managing calendar callbacks
 */
export const useCalendarCallbacks = (
  options: UseCalendarCallbacksOptions
): UseCalendarCallbacksReturn => {
  const {
    onDateChange,
    onModeChange,
    onDaySelect,
    onEventClick,
    onPopupLeftAction,
    onAddOrEditEventClick,
    onDeleteEventClick,
    customClass,
    customAriaLabel,
    customIconClass,
  } = options;
  
  // Handle day click - opens popup or triggers selection
  const handleDayClick = useCallback((
    day: DayObject,
    events: CalendarEvent[],
    _event: React.MouseEvent
  ) => {
    onDaySelect?.({
      dt: day,
      events,
    });
  }, [onDaySelect]);
  
  // Handle day select - for date picking scenarios
  const handleDaySelect = useCallback((
    day: DayObject,
    events: CalendarEvent[]
  ) => {
    const data: DaySelectCallbackData = {
      dt: day,
      events,
    };
    onDaySelect?.(data);
  }, [onDaySelect]);
  
  // Handle event click
  const handleEventClick = useCallback((
    calendarEvent: CalendarEvent,
    day: DayObject,
    event: React.MouseEvent
  ) => {
    // Prevent event click from triggering day click (guard for synthetic events)
    event?.stopPropagation?.();
    
    const data: EventClickCallbackData = {
      dt: day.date,
      event: calendarEvent,
    };
    onEventClick?.(data);
  }, [onEventClick]);
  
  // Handle date change (navigation)
  const handleDateChange = useCallback((date: Date) => {
    onDateChange?.(date);
  }, [onDateChange]);
  
  // Handle mode change
  const handleModeChange = useCallback((mode: CalendarMode, activeDate: Date) => {
    const data: ModeChangeCallbackData = {
      mode,
      activeDate,
    };
    onModeChange?.(data);
  }, [onModeChange]);
  
  // Handle add event click (from popup or day)
  const handleAddEventClick = useCallback((
    day: DayObject,
    _event: React.MouseEvent
  ) => {
    const data: AddEditEventCallbackData = {
      event: null, // null indicates new event
      forDate: day.date,
    };
    onAddOrEditEventClick?.(data);
  }, [onAddOrEditEventClick]);
  
  // Handle edit event click
  const handleEditEventClick = useCallback((
    calendarEvent: CalendarEvent,
    day: DayObject
  ) => {
    const data: AddEditEventCallbackData = {
      event: calendarEvent,
      forDate: day.date,
    };
    onAddOrEditEventClick?.(data);
  }, [onAddOrEditEventClick]);

  const handleDeleteEventClick = useCallback((
    calendarEvent: CalendarEvent,
    day: DayObject
  ) => {
    const data: DeleteEventCallbackData = {
      event: calendarEvent,
      dateId: day.key,
    };
    onDeleteEventClick?.(data);
  }, [onDeleteEventClick]);
  
  // Handle popup left action (typically "Add Events")
  const handlePopupLeftAction = useCallback((date: Date, events: CalendarEvent[]) => {
    const data: PopupActionCallbackData = {
      data: {
        dt: date,
        events,
      },
    };
    onPopupLeftAction?.(data);
  }, [onPopupLeftAction]);
  
  // Get custom class for a day
  const getDayCustomClass = useCallback((date: Date, mode: string): string | undefined => {
    if (customClass) {
      return customClass({ date, mode }) || undefined;
    }
    return undefined;
  }, [customClass]);
  
  // Get custom aria label for a day
  const getDayCustomAriaLabel = useCallback((date: Date, mode: string): string | undefined => {
    if (customAriaLabel) {
      return customAriaLabel({ date, mode }) || undefined;
    }
    return undefined;
  }, [customAriaLabel]);
  
  // Get custom icon class for a day
  const getDayCustomIconClass = useCallback((date: Date, mode: string): string | undefined => {
    if (customIconClass) {
      return customIconClass({ date, mode }) || undefined;
    }
    return undefined;
  }, [customIconClass]);
  
  return {
    handleDayClick,
    handleDaySelect,
    handleEventClick,
    handleDateChange,
    handleModeChange,
    handleAddEventClick,
    handleEditEventClick,
    handleDeleteEventClick,
    handlePopupLeftAction,
    getDayCustomClass,
    getDayCustomAriaLabel,
    getDayCustomIconClass,
  };
};

export default useCalendarCallbacks;
